import api from '@pray/shared/utils/api';

import type { ImportSeedsResponse, SeedSourceValue } from './types';

const IMPORT_URL = '/web/studio/admin/seed-emails/seeds/import';

/**
 * At most one group: the server refuses a request that names both, so the type does not allow it.
 * `groupId` is an existing group every address joins, `newGroupName` a group the import creates.
 * The other field is `?: undefined`, not `?: never`: libs/shared is not strict, and without
 * strictNullChecks `never` refuses an explicit `undefined`.
 */
type ImportGroupFields =
  | { groupId?: string; newGroupName?: undefined }
  | { groupId?: undefined; newGroupName?: string };

export type PostImportSeedsParams = {
  emails: string[];
  source?: SeedSourceValue;
} & ImportGroupFields;

/**
 * Imports the addresses the Upload CSV dialog parsed in the browser. The file itself never leaves
 * the machine and there is no upload step: the request body is the address list.
 *
 * The answer is a report, not a list. An address that is already a seed is counted, not refused,
 * so re-uploading a file that overlaps the list is a normal thing to do.
 *
 * With a group, the membership write happens in the import's transaction: a group that is gone or
 * a name that is taken refuses the whole request and nothing is imported. The group fields are
 * left out of the body when unset, so an import without one sends exactly what it always did.
 */
export async function postImportSeeds({ emails, source = 'other', groupId, newGroupName }: PostImportSeedsParams) {
  const res = await api.executePostRequest<{ data: ImportSeedsResponse }>(IMPORT_URL, {
    emails,
    source,
    ...(groupId ? { group_id: groupId } : {}),
    ...(newGroupName ? { new_group_name: newGroupName } : {}),
  });
  return res.data.data;
}
